"use client";

import { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@libs/zapi-ui/shadcn";
import { AppButton, SelectionDropdown } from "@libs/zapi-ui/custom";
import { useOrganizationStore, type Organization } from "@/zapi-frontend/stores/organization";
import { useMeStore } from "@/zapi-frontend/shared/hooks/useMe";
import { isSystemAdmin } from "@/zapi-frontend/shared/utils/permissions";

type TDuplicateProjectModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (organizationId: number) => Promise<void>;
  currentOrganizationId?: number;
};

const getOptionValue = (org: Organization) => `${org.name}:::${org.id}`.toLowerCase();

const getOrgIdFromValue = (value: string) => {
  if (!value) return null;
  const parts = value.split(":::");
  return parts.length > 1 ? Number(parts[parts.length - 1]) : null;
};

export function DuplicateProjectModal(props: TDuplicateProjectModalProps) {
  const { isOpen, onClose, onConfirm, currentOrganizationId } = props;
  const { availableOrganizations } = useOrganizationStore();
  const role = useMeStore((s) => s.role);
  const canPickOrg = isSystemAdmin(role);
  const [selectedOptionValue, setSelectedOptionValue] = useState<string>("");
  const [isDuplicating, setIsDuplicating] = useState(false);

  useEffect(() => {
    let defaultOrg: Organization | undefined;

    if (currentOrganizationId) defaultOrg = availableOrganizations.find((o) => o.id === currentOrganizationId);

    if (!defaultOrg && availableOrganizations.length > 0) defaultOrg = availableOrganizations[0];

    if (defaultOrg) setSelectedOptionValue(getOptionValue(defaultOrg));
  }, [currentOrganizationId, availableOrganizations]);

  async function handleConfirm() {
    const orgId = canPickOrg ? getOrgIdFromValue(selectedOptionValue) : currentOrganizationId;
    if (!orgId) return;

    setIsDuplicating(true);
    await onConfirm(orgId);
    setIsDuplicating(false);
    closeHandler();
  }

  const options = useMemo(
    () =>
      availableOrganizations.map((org) => ({
        value: getOptionValue(org),
        label: `${org.name} (ID: ${org.id})`
      })),
    [availableOrganizations]
  );

  function closeHandler() {
    setSelectedOptionValue("");
    onClose();
  }

  return (
    <Dialog open={isOpen} onOpenChange={closeHandler}>
      <DialogContent onClick={(e) => e.stopPropagation()} className="max-w-md">
        <DialogHeader>
          <DialogTitle>Duplicate Project</DialogTitle>
          {canPickOrg ? (
            <DialogDescription>Select the organization where you want to copy this project.</DialogDescription>
          ) : (
            <DialogDescription>Are you sure you want to duplicate this project?</DialogDescription>
          )}
        </DialogHeader>

        {canPickOrg && (
          <div className="space-y-4 py-4">
            <SelectionDropdown
              label="Organization"
              placeholder="Select an organization"
              options={options}
              value={selectedOptionValue}
              onChange={setSelectedOptionValue}
              disabled={isDuplicating}
              modal
            />
          </div>
        )}

        <DialogFooter>
          <AppButton appearance="outlined" onClick={closeHandler} disabled={isDuplicating}>
            Cancel
          </AppButton>
          <AppButton
            onClick={handleConfirm}
            isLoading={isDuplicating}
            disabled={canPickOrg ? !selectedOptionValue : !currentOrganizationId}
          >
            Duplicate
          </AppButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
